import { apiService } from './api';

export interface LaboratoryMethod {
  id: string;
  method_name: string;
  method_code: string;
  laboratory: string;
  category: string;
  is_accredited: boolean;
  elements: string[];
  typical_tat_hours?: number;
  cost_per_sample?: number;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  display_name: string;
  laboratory_display: string;
  accreditation_status: string;
}

export interface MethodSelectionResult {
  mechanicalPrepRequired: boolean;
  selectedMechanicalMethods: string[];
  selectedAnalysisMethods: string[];
  nextLocation: string;
  workflowPath: string[];
}

export interface AccreditationSummary {
  laboratory: string;
  totalMethods: number;
  accreditedMethods: number;
  accreditationPercentage: number;
  methodsList: LaboratoryMethod[];
}

export interface WorkflowRequirements {
  requiresMechanicalPrep: boolean;
  mechanicalPrepMethods: LaboratoryMethod[];
  analysisMethods: LaboratoryMethod[];
  estimatedTotalHours: number;
  estimatedTotalCost: number;
}

export interface MethodStatistics {
  totalMethods: number;
  accreditedMethods: number;
  accreditationRate: number;
  methodsByLaboratory: Record<string, number>;
  methodsByCategory: Record<string, number>;
}

export class LaboratoryMethodsService {
  private baseUrl = '/laboratory-methods';

  /**
   * Get all laboratory methods
   */
  async getAllMethods(): Promise<LaboratoryMethod[]> {
    try {
      const response = await apiService.get<LaboratoryMethod[]>(this.baseUrl);
      return response;
    } catch (error) {
      console.error('Error fetching laboratory methods:', error);
      throw error;
    }
  }

  /**
   * Get methods by laboratory type
   */
  async getMethodsByLaboratory(laboratory: string): Promise<LaboratoryMethod[]> {
    const response = await apiService.get<LaboratoryMethod[]>(`${this.baseUrl}/by-laboratory/${laboratory}`);
    return response;
  }

  /**
   * Get mechanical preparation methods
   */
  async getMechanicalPrepMethods(): Promise<LaboratoryMethod[]> {
    const response = await apiService.get<LaboratoryMethod[]>(`${this.baseUrl}/mechanical-prep`);
    return response;
  }

  /**
   * Get analysis methods (non-mechanical prep)
   */
  async getAnalysisMethods(): Promise<LaboratoryMethod[]> {
    const response = await apiService.get<LaboratoryMethod[]>(`${this.baseUrl}/analysis-methods`);
    return response;
  }

  /**
   * Get accredited methods only
   */
  async getAccreditedMethods(): Promise<LaboratoryMethod[]> {
    const response = await apiService.get<LaboratoryMethod[]>(`${this.baseUrl}/accredited`);
    return response;
  }

  /**
   * Get methods grouped by laboratory
   */
  async getMethodsGroupedByLaboratory(): Promise<Record<string, LaboratoryMethod[]>> {
    const response = await apiService.get<Record<string, LaboratoryMethod[]>>(`${this.baseUrl}/grouped-by-laboratory`);
    return response;
  }

  /**
   * Get accreditation summary by laboratory
   */
  async getAccreditationSummary(): Promise<AccreditationSummary[]> {
    const response = await apiService.get<AccreditationSummary[]>(`${this.baseUrl}/accreditation-summary`);
    return response;
  }

  /**
   * Get method statistics
   */
  async getMethodStatistics(): Promise<MethodStatistics> {
    const response = await apiService.get<MethodStatistics>(`${this.baseUrl}/statistics`);
    return response;
  }

  /**
   * Validate method selection and get workflow requirements
   */
  async validateMethodSelection(methodCodes: string[]): Promise<MethodSelectionResult> {
    const response = await apiService.post<MethodSelectionResult>(`${this.baseUrl}/validate-selection`, {
      methodCodes,
    });
    return response;
  }

  /**
   * Get workflow requirements for selected methods
   */
  async getWorkflowRequirements(methodCodes: string[]): Promise<WorkflowRequirements> {
    const response = await apiService.post<WorkflowRequirements>(`${this.baseUrl}/workflow-requirements`, {
      methodCodes,
    });
    return response;
  }

  /**
   * Search methods by name or code
   */
  async searchMethods(query: string): Promise<LaboratoryMethod[]> {
    const response = await apiService.get<LaboratoryMethod[]>(`${this.baseUrl}/search`, {
      params: { q: query },
    });
    return response;
  }

  /**
   * Get methods by codes
   */
  async getMethodsByCodes(codes: string[]): Promise<LaboratoryMethod[]> {
    const response = await apiService.get<LaboratoryMethod[]>(`${this.baseUrl}/by-codes`, {
      params: { codes: codes.join(',') },
    });
    return response;
  }

  /**
   * Get method by code
   */
  async getMethodByCode(code: string): Promise<LaboratoryMethod> {
    const response = await apiService.get<LaboratoryMethod>(`${this.baseUrl}/${code}`);
    return response;
  }

  /**
   * Helper method to group methods by laboratory for frontend display
   */
  groupMethodsByLaboratory(methods: LaboratoryMethod[]): Record<string, LaboratoryMethod[]> {
    return methods.reduce((grouped, method) => {
      const lab = method.laboratory;
      if (!grouped[lab]) {
        grouped[lab] = [];
      }
      grouped[lab].push(method);
      return grouped;
    }, {} as Record<string, LaboratoryMethod[]>);
  }

  /**
   * Helper method to filter accredited methods
   */
  filterAccreditedMethods(methods: LaboratoryMethod[]): LaboratoryMethod[] {
    return methods.filter(method => method.is_accredited);
  }

  /**
   * Helper method to get laboratory display name
   */
  getLaboratoryDisplayName(laboratory: string): string {
    const displayNames: Record<string, string> = {
      'PREPARATION_MECANIQUE': 'Préparation Mécanique',
      'LABORATOIRE_SAA': 'Laboratoire SAA',
      'LABORATOIRE_ICP': 'Laboratoire ICP',
      'LABORATOIRE_AU': 'Laboratoire d\'Analyse Au',
      'LABORATOIRE_VOLUMETRIE': 'Laboratoire Volumétrie',
    };
    return displayNames[laboratory] || laboratory.replace('LABORATOIRE_', '').replace('_', ' ');
  }

  /**
   * Helper method to format method for display
   */
  formatMethodForDisplay(method: LaboratoryMethod): string {
    return `${method.method_name} (${method.method_code})${method.is_accredited ? ' ✓ ACCREDITÉ' : ''}`;
  }

  /**
   * Helper method to calculate total cost and time for selected methods
   */
  calculateTotals(methods: LaboratoryMethod[]): { totalCost: number; totalHours: number } {
    return methods.reduce(
      (totals, method) => ({
        totalCost: totals.totalCost + (method.cost_per_sample || 0),
        totalHours: totals.totalHours + (method.typical_tat_hours || 0),
      }),
      { totalCost: 0, totalHours: 0 }
    );
  }
}

export const laboratoryMethodsService = new LaboratoryMethodsService(); 